import { parseWithZod } from '@conform-to/zod'
import { invariantResponse } from '@epic-web/invariant'
import { motion } from 'framer-motion'
import { Img } from 'openimg/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { data, Link } from 'react-router'
import { z } from 'zod'
import { ConfirmationDialog } from '#app/components/confirmation-dialog.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { fadeInUp } from '#app/utils/animations.ts'
import { getUserId } from '#app/utils/auth.server.ts'
import { invalidateBeeldCaches } from '#app/utils/beelden-cache.server.ts'
import { cachified, lruCache } from '#app/utils/cache.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { pipeHeaders } from '#app/utils/headers.server.ts'
import { getLocale } from '#app/utils/locale.server.ts'
import { getNoteImgSrc } from '#app/utils/misc.tsx'
import { buildMeta } from '#app/utils/seo.ts'
import { titleToSlug } from '#app/utils/slug.ts'
import { redirectWithToast } from '#app/utils/toast.server.ts'
import { useOptionalUser } from '#app/utils/user.ts'
import { type Route } from './+types/beelden.ts'

const DeleteBeeldSchema = z.object({
	intent: z.literal('delete-beeld'),
	beeldId: z.string(),
})

export const handle = { i18n: 'common' }

export async function loader({ request }: Route.LoaderArgs) {
	const language = await getLocale(request)

	const notes = await cachified({
		key: `beelden-gallery-${language}`,
		cache: lruCache,
		ttl: 1000 * 60 * 5,
		staleWhileRevalidate: 1000 * 60 * 60,
		async getFreshValue() {
			const raw = await prisma.note.findMany({
				select: {
					id: true,
					title: true,
					category: true,
					owner: { select: { username: true, name: true } },
					images: {
						select: { id: true, objectKey: true, altText: true },
						take: 1,
					},
					translations: {
						where: { language },
						select: { title: true },
					},
				},
				where: { images: { some: {} } },
				orderBy: { updatedAt: 'desc' },
			})

			return raw
				.filter((note) => note.images.length > 0)
				.map((note) => ({
					id: note.id,
					title: note.translations[0]?.title || note.title,
					category: note.category,
					owner: note.owner,
					images: note.images,
				}))
		},
	})

	return data(
		{ notes },
		{
			headers: {
				'Cache-Control':
					'public, max-age=60, s-maxage=300, stale-while-revalidate=3600',
			},
		},
	)
}

export const headers: Route.HeadersFunction = pipeHeaders

export async function action({ request }: Route.ActionArgs) {
	const userId = await getUserId(request)
	if (!userId) {
		throw data({ error: 'Unauthorized' }, { status: 401 })
	}

	const formData = await request.formData()
	const submission = parseWithZod(formData, {
		schema: DeleteBeeldSchema,
	})

	if (submission.status !== 'success') {
		return data(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	const { beeldId } = submission.value

	const note = await prisma.note.findFirst({
		select: { id: true, title: true },
		where: { id: beeldId },
	})
	invariantResponse(note, 'Beeld niet gevonden', { status: 404 })

	// Allow any logged-in user to delete any note
	await prisma.note.delete({ where: { id: beeldId } })
	invalidateBeeldCaches({ noteId: beeldId })

	return redirectWithToast('/beelden', {
		type: 'success',
		title: 'Success',
		description: `"${note.title}" is verwijderd.`,
	})
}

export default function BeeldenGalleryRoute({
	loaderData,
}: Route.ComponentProps) {
	const user = useOptionalUser()
	const { t } = useTranslation()
	const canDelete = Boolean(user)
	const [filter, setFilter] = useState<'all' | 'autonoom' | 'drijvend' | 'openbare_ruimte'>('all')

	const allNotes = loaderData.notes
	const filteredNotes = filter === 'all'
		? allNotes
		: allNotes.filter((n) => n.category === filter)
	const hasAnyNotes = allNotes.length > 0

	return (
		<div data-testid="gallery-container">
			{/* Header */}
			<motion.section
				className="pt-[120px] pb-16"
				initial="initial"
				whileInView="animate"
				viewport={{ once: true, amount: 0.15 }}
				variants={fadeInUp}
			>
				<div className="container">
					<div className="eyebrow mb-4">Catalogus · {allNotes.length} werken</div>
					<h1 className="display mb-8" style={{ fontSize: 'clamp(48px, 8vw, 112px)', maxWidth: '14ch' }}>
						Werken<br />
						<span style={{ color: 'var(--muted-gallery)' }}>1986 tot nu</span>
					</h1>

					{/* Tab filters */}
					<div className="flex gap-6" style={{ borderBottom: '1px solid var(--line)' }}>
						{([
							{ k: 'all' as const, l: t('categoryAll') },
							{ k: 'autonoom' as const, l: t('categoryAutonomous') },
							{ k: 'drijvend' as const, l: t('categoryFloating') },
							{ k: 'openbare_ruimte' as const, l: t('categoryPublicSpace') },
						]).map((f) => (
							<button
								key={f.k}
								onClick={() => setFilter(f.k)}
								className="cursor-pointer border-b-2 pb-3 text-sm transition-colors"
								style={{
									background: 'none',
									border: 'none',
									borderBottom: filter === f.k ? '2px solid var(--ink)' : '2px solid transparent',
									color: filter === f.k ? 'var(--ink)' : 'var(--muted-gallery)',
									marginBottom: -1,
									padding: '12px 0',
									fontFamily: 'var(--font-sans)',
									fontSize: 14,
								}}
							>
								{f.l}
							</button>
						))}
					</div>

					{user && (
						<div className="mt-6">
							<Link to={`/users/${user.username}/notes/new`} className="btn-gallery">
								<Icon name="plus" className="h-4 w-4" />
								{t('addNewSculpture')}
							</Link>
						</div>
					)}
				</div>
			</motion.section>

			{/* Gallery Grid */}
			<section className="pb-[120px] pt-10">
				<div className="container">
					{hasAnyNotes ? (
						<div className="masonry-grid">
							{filteredNotes.map((note) => {
								const image = note.images[0]
								if (!image) return null
								const slug = titleToSlug(note.title)

								return (
									<motion.article
										key={note.id}
										initial="initial"
										whileInView="animate"
										viewport={{ once: true, amount: 0.15 }}
										variants={fadeInUp}
										className="group relative mb-8 cursor-pointer break-inside-avoid"
									>
										{canDelete && (
											<div className="absolute top-2 right-2 z-10 opacity-0 transition-opacity group-hover:opacity-100">
												<DeleteBeeldButton beeldId={note.id} beeldTitle={note.title} />
											</div>
										)}
										<Link
											to={`/beelden/${slug}`}
											className="block"
											discover="none"
										>
											<div className="relative overflow-hidden" style={{ background: 'var(--paper-2)' }}>
												<ImageWithLoader
													src={getNoteImgSrc(image.objectKey)}
													alt={image.altText ?? note.title}
													title={note.title}
												/>
											</div>
											<div className="flex items-baseline justify-between gap-3 pt-3">
												<div>
													<h3 className="font-heading text-[17px] font-medium italic" style={{ letterSpacing: '-0.01em' }}>
														{note.title}
													</h3>
													<div className="eyebrow mt-1">
														{note.category === 'autonoom' && t('categoryAutonomous')}
														{note.category === 'drijvend' && t('categoryFloating')}
														{note.category === 'openbare_ruimte' && t('categoryPublicSpace')}
													</div>
												</div>
											</div>
										</Link>
									</motion.article>
								)
							})}
						</div>
					) : (
						<div className="py-24 text-center">
							<Icon name="camera" className="mx-auto mb-4 h-12 w-12" style={{ color: 'var(--muted-gallery)' }} />
							<h3 className="font-heading mb-2 text-xl font-medium italic">
								Nog geen beelden
							</h3>
							<p style={{ color: 'var(--muted-gallery)' }}>
								Wanneer beelden worden toegevoegd, verschijnen ze hier.
							</p>
						</div>
					)}
				</div>
			</section>
		</div>
	)
}

function DeleteBeeldButton({
	beeldId,
	beeldTitle,
}: {
	beeldId: string
	beeldTitle: string
}) {
	const { t } = useTranslation()

	return (
		<ConfirmationDialog
			trigger={
				<Button
					variant="destructive"
					size="sm"
					className="h-8 w-8 p-0 shadow-lg"
					type="button"
				>
					<Icon name="trash" className="h-4 w-4" />
					<span className="sr-only">Verwijder {beeldTitle}</span>
				</Button>
			}
			title={t('delete')}
			description={`${t('confirmDelete')} "${beeldTitle}"?`}
			confirmText={t('delete')}
			formData={{
				intent: 'delete-beeld',
				beeldId: beeldId,
			}}
		/>
	)
}

function ImageWithLoader({
	src,
	alt,
	title,
}: {
	src: string
	alt: string
	title: string
}) {
	return (
		<>
			<Img
				src={src}
				alt={alt}
				className="block h-auto w-full origin-center transform-gpu transition-transform duration-700 group-hover:scale-[1.06]"
				width={400}
				height={400}
				fit="cover"
				style={{ transitionTimingFunction: 'var(--ease)' }}
			/>
			<div className="absolute inset-x-0 bottom-0 translate-y-full bg-gradient-to-t from-black/70 via-black/30 to-transparent p-4 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100"
				style={{ transitionTimingFunction: 'var(--ease)' }}
			>
				<h3 className="font-heading text-lg leading-tight font-medium italic text-white">
					{title}
				</h3>
			</div>
		</>
	)
}

export const meta: Route.MetaFunction = () => {
	return buildMeta({
		title: 'Werken',
		description:
			'Bekijk het volledige oeuvre van beeldend kunstenaar Else Ringnalda. Bronzen en stenen sculpturen van 1986 tot nu.',
		url: '/beelden',
	})
}
